import React, { useState, useEffect } from 'react';
import usePageTitle from '@/hooks/usePageTitle';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ChevronDown, ChevronUp, MapPin, User, Info, Trophy, Clock, ChevronLeft, X, Video } from 'lucide-react';
import { formatUKDate, formatUKTime } from '@/utils/ukTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Privacy Notice Modal Content
function PrivacyNoticeModal({ isOpen, onClose }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex flex-col items-center gap-2">
            <img src="/scorz-logo.png" alt="Scorz Logo" className="h-16 w-auto" />
            <DialogTitle className="text-2xl font-bold text-center">
              Privacy Notice
            </DialogTitle>
            <p className="text-sm text-gray-500">Last updated: January 2026</p>
          </div>
        </DialogHeader>

        <div className="space-y-6 text-sm">
          <section>
            <p className="text-gray-700">
              Scorz is a competition management and scoring platform used to administer gymnastics competitions. Scorz is a trading name of <strong>Mullens Ltd</strong> (Company No. 17109449).
            </p>
            <p className="text-gray-700 mt-3">
              This Privacy Notice explains how personal data and related files are collected, used, stored, and protected when using the Scorz platform.
            </p>
          </section>

          <hr className="border-gray-300" />

          <section>
            <h3 className="text-lg font-semibold mb-3">1. Data Collected</h3>
            <p className="mb-3 text-gray-700">
              Scorz collects and stores personal data and competition-related files strictly for competition administration and governance purposes.
            </p>
            <p className="mb-2 font-medium">The data collected may include:</p>
            
            <div className="space-y-4 ml-4">
              <div>
                <p className="font-medium">Gymnasts (including children):</p>
                <ul className="list-disc ml-6 mt-1 space-y-1 text-gray-700">
                  <li>Full name</li>
                  <li>Date of birth</li>
                  <li>British Gymnastics membership number</li>
                </ul>
              </div>

              <div>
                <p className="font-medium">Coaches:</p>
                <ul className="list-disc ml-6 mt-1 space-y-1 text-gray-700">
                  <li>Full name</li>
                  <li>Date of birth</li>
                  <li>Qualification details</li>
                  <li>British Gymnastics membership number</li>
                </ul>
              </div>

              <div>
                <p className="font-medium">Judges:</p>
                <ul className="list-disc ml-6 mt-1 space-y-1 text-gray-700">
                  <li>Full name</li>
                  <li>Date of birth</li>
                  <li>Qualification details</li>
                  <li>British Gymnastics membership number</li>
                </ul>
              </div>

              <div>
                <p className="font-medium">Competition and System Data:</p>
                <ul className="list-disc ml-6 mt-1 space-y-1 text-gray-700">
                  <li>Competition location (address)</li>
                  <li>Competition programme details, including start and finish times</li>
                  <li>Competition entry information</li>
                  <li>Gymnast floor music files (MP3 or similar formats)</li>
                  <li>Uploaded payment proof and supporting documents</li>
                  <li>Other files or data uploaded as part of competition administration</li>
                </ul>
              </div>
            </div>
          </section>

          <hr className="border-gray-300" />

          <section>
            <h3 className="text-lg font-semibold mb-3">2. Why the Data Is Used</h3>
            <p className="mb-2 text-gray-700">Personal data and uploaded files within Scorz are used solely for:</p>
            <ul className="list-disc ml-6 space-y-1 text-gray-700">
              <li>Administration and management of gymnastics competitions</li>
              <li>Verification of eligibility, qualifications, and membership</li>
              <li>Safeguarding and governance requirements</li>
              <li>Management of competition-related materials, including gymnast-specific floor music</li>
              <li>Verification of payments and competition entries</li>
              <li>Communication with clubs in relation to competitions and events</li>
            </ul>
            <p className="mt-3 font-medium text-gray-900">Personal data is not used for marketing purposes.</p>
          </section>

          <hr className="border-gray-300" />

          <section>
            <h3 className="text-lg font-semibold mb-3">3. Lawful Basis for Processing</h3>
            <p className="mb-3 text-gray-700">
              Scorz processes personal data in order to administer gymnastics competitions safely and effectively.
            </p>
            <p className="mb-2 font-medium">The lawful basis for processing is:</p>
            <ul className="list-disc ml-6 space-y-1 text-gray-700">
              <li><strong>Legitimate interests</strong> – to run, administer, and manage competitions properly</li>
              <li><strong>Legal and governance obligations</strong> – including safeguarding, eligibility checking, and compliance with British Gymnastics and regional requirements</li>
            </ul>
            <p className="mt-3 text-gray-700">Only the data necessary for these purposes is collected and processed.</p>
          </section>

          <hr className="border-gray-300" />

          <section>
            <h3 className="text-lg font-semibold mb-3">4. Data Controller and Responsibilities</h3>
            <p className="mb-2 text-gray-700">For the purposes of data protection law:</p>
            <ul className="list-disc ml-6 space-y-1 mb-4 text-gray-700">
              <li><strong>Mullens Ltd</strong> (Company No. 17109449), trading as Scorz, is the Data Controller for the Scorz platform.</li>
              <li>Freddie Mullen, acting on behalf of Mullens Ltd, provides system support and assistance relating to user accounts and technical issues.</li>
            </ul>
            
            <p className="font-medium mb-2">Role of Clubs</p>
            <p className="mb-2 text-gray-700">Clubs act as data providers within the system.</p>
            <p className="mb-2 text-gray-700">Clubs are responsible for:</p>
            <ul className="list-disc ml-6 space-y-1 text-gray-700">
              <li>Ensuring the accuracy of the data they upload</li>
              <li>Keeping gymnast, coach, and judge information up to date</li>
              <li>Removing individuals they no longer have a legitimate reason to retain within the system</li>
            </ul>
          </section>

          <hr className="border-gray-300" />

          <section>
            <h3 className="text-lg font-semibold mb-3">5. Platform Hosting, Storage, and Security</h3>
            <p className="mb-3 text-gray-700">
              All data within the Scorz platform is hosted and stored using <strong>Cloudflare</strong>.
            </p>
            <p className="mb-2 text-gray-700">This includes, but is not limited to:</p>
            <ul className="list-disc ml-6 space-y-1 mb-4 text-gray-700">
              <li>Personal data records</li>
              <li>Competition entry information</li>
              <li>Gymnast floor music files (MP3s)</li>
              <li>Uploaded payment proof and supporting documents</li>
              <li>Any other files or data uploaded to the system</li>
            </ul>
            <p className="mb-3 text-gray-700">
              Cloudflare provides both hosting infrastructure and security services, including encrypted data transmission, protection against unauthorised access, malicious activity, and network attacks.
            </p>
            <p className="mb-3 text-gray-700">
              Cloudflare acts as a data processor on behalf of Mullens Ltd and processes data only in accordance with the Data Controller's instructions and appropriate contractual and security safeguards.
            </p>
            <p className="text-gray-700">
              Appropriate technical and organisational measures are in place to protect all stored data and ensure system reliability and security.
            </p>
          </section>

          <hr className="border-gray-300" />

          <section>
            <h3 className="text-lg font-semibold mb-3">6. Contact Details</h3>
            <p className="mb-3 text-gray-700">
              For questions relating to data protection, account management, or this Privacy Notice, please contact:
            </p>
            <p className="text-lg font-medium">
              <a href="mailto:support@scorz.co.uk" className="text-black hover:underline">support@scorz.co.uk</a>
            </p>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Gymnast Score Detail Modal
function ScoreDetailModal({ isOpen, onClose, gymnast, apparatus, score, scoreConfig }) {
  if (!score) return null;
  
  const gymnastName = score?.gymnastName || gymnast?.gymnastName || gymnast?.preferredName || 
                      gymnast?.name || `${gymnast?.firstName || ''} ${gymnast?.lastName || ''}`.trim() || 'Unknown';
  const gymnastNumber = score?.gymnastNumber || gymnast?.gymnastNumber || gymnast?.number || '-';
  const clubName = score?.clubName || gymnast?.clubName || gymnast?.club || '-';
  const apparatusDisplay = (apparatus || score?.apparatus || '').toUpperCase();
  const components = scoreConfig?.scoreComponents || { D: true, E: true, B: false, ND: true };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xs p-0 overflow-hidden rounded-lg shadow-2xl border-0">
        <div className="bg-white">
          <div className="bg-white px-6 py-4 text-center border-b border-gray-200">
            <img src="/scorz-logo.png" alt="Scorz" className="h-10 mx-auto" />
            <p className="text-gray-800 text-sm font-semibold tracking-wider uppercase mt-2">{apparatusDisplay}</p>
          </div>
          
          <div className="px-6 py-4 border-b border-dashed border-gray-300 text-center">
            <p className="text-4xl font-bold text-black mb-1">{gymnastNumber}</p>
            <p className="text-lg font-semibold text-gray-900">{gymnastName}</p>
            <p className="text-sm text-gray-500">{clubName}</p>
          </div>
          
          <div className="px-6 py-4 space-y-3 font-mono text-sm">
            {components.D && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">D Score</span>
              <span className="font-semibold text-gray-900">{parseFloat(score.difficulty || 0).toFixed(3)}</span>
            </div>
            )}
            {components.E && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">E Score</span>
              <span className="font-semibold text-gray-900">{parseFloat(score.execution || 0).toFixed(3)}</span>
            </div>
            )}
            {components.B && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Bonus</span>
              <span className="font-semibold text-green-600">+{parseFloat(score.bonus || 0).toFixed(3)}</span>
            </div>
            )}
            {components.ND && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Neutral Ded.</span>
              <span className="font-semibold text-red-600">-{parseFloat(score.neutralDeductions || 0).toFixed(3)}</span>
            </div>
            )}
            
            <div className="border-t border-gray-300 my-2"></div>
            
            <div className="flex justify-between items-center pt-1">
              <span className="text-gray-900 font-bold text-base">FINAL</span>
              <span className="font-bold text-2xl text-black">{parseFloat(score.finalScore || 0).toFixed(3)}</span>
            </div>
          </div>
          
          <div className="px-6 pb-4">
            <button onClick={() => onClose(false)} className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded transition-colors">
              Close
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    current: true,
    future: false,
    past: false
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [privacyOpen, setPrivacyOpen] = useState(false);
  
  // Page title
  usePageTitle('Home');
  
  // System status state
  const [systemStatus, setSystemStatus] = useState({ statusText: 'All Systems Operational', statusColor: 'green', maintenanceMode: false });
  
  // Live Rankings state
  const [categories, setCategories] = useState([]);
  const [teamCategories, setTeamCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isTeamCategory, setIsTeamCategory] = useState(false);
  const [selectedApparatus, setSelectedApparatus] = useState('vault');
  const [rankings, setRankings] = useState([]);
  const [teamRankings, setTeamRankings] = useState(null);
  const [loadingRankings, setLoadingRankings] = useState(false);
  const [qualification, setQualification] = useState(null); // {enabled, text, score}
  
  // Latest Scores state
  const [latestScores, setLatestScores] = useState([]);
  const [loadingScores, setLoadingScores] = useState(false);
  
  // Filter state for Latest Scores and Live Rankings
  const [clubs, setClubs] = useState([]);
  const [filterClub, setFilterClub] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  
  // Score detail modal
  const [scoreDetailOpen, setScoreDetailOpen] = useState(false);
  const [selectedGymnastDetail, setSelectedGymnastDetail] = useState(null);
  const [selectedScoreDetail, setSelectedScoreDetail] = useState(null);
  const [selectedApparatusForModal, setSelectedApparatusForModal] = useState(null);
  
  // Live Stream dialog
  const [showLiveStreamDialog, setShowLiveStreamDialog] = useState(false);
  
  // Auto-refresh interval ref
  const refreshIntervalRef = React.useRef(null);
  // Track if this is initial load (show loading) vs auto-refresh (don't show loading)
  const isInitialRankingsLoad = React.useRef(true);
  const isInitialScoresLoad = React.useRef(true);

  useEffect(() => {
    fetchCompetitions();
    fetchSystemStatus();
  }, []);

  const fetchSystemStatus = async () => {
    try {
      const res = await axios.get(`${API}/public/system-status`);
      setSystemStatus(res.data);
    } catch {
      // Default to operational
    }
  };

  // Fetch categories when event is selected
  useEffect(() => {
    if (selectedEvent) {
      // Fetch categories with splits applied
      const fetchCategories = async () => {
        try {
          const res = await axios.get(`${API}/public/categories/${selectedEvent.id}`);
          setCategories(res.data.categories || []);
          setTeamCategories(res.data.teamCategories || []);
        } catch (error) {
          // Fallback to competition categories if endpoint fails
          setCategories(selectedEvent.categories || []);
          setTeamCategories([]);
        }
      };
      
      // Fetch clubs for this competition
      const fetchClubs = async () => {
        try {
          const res = await axios.get(`${API}/public/clubs/${selectedEvent.id}`);
          setClubs(res.data.clubs || []);
        } catch (error) {
          setClubs([]);
        }
      };
      
      fetchCategories();
      fetchClubs();
      setSelectedCategory(null);
      setIsTeamCategory(false);
      setRankings([]);
      setTeamRankings(null);
      setLatestScores([]);
      setFilterClub('');
      setFilterGrade('');
    }
  }, [selectedEvent]);

  // Fetch rankings when category or apparatus changes + auto-refresh
  useEffect(() => {
    if (selectedEvent && selectedCategory && activeTab === 'rankings') {
      if (isTeamCategory) {
        isInitialRankingsLoad.current = true;
        fetchTeamRankings();
        refreshIntervalRef.current = setInterval(() => fetchTeamRankings(), 10000);
      } else {
        isInitialRankingsLoad.current = true;
        fetchRankings();
        refreshIntervalRef.current = setInterval(() => fetchRankings(), 5000);
      }
      return () => { if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current); };
    }
  }, [selectedEvent, selectedCategory, selectedApparatus, activeTab, isTeamCategory]);

  // Fetch latest scores when tab is active + auto-refresh
  useEffect(() => {
    if (selectedEvent && activeTab === 'scores') {
      // Reset initial load flag when switching to scores tab (show loading for new data)
      isInitialScoresLoad.current = true;
      fetchLatestScores();
      
      // Set up auto-refresh every 5 seconds
      refreshIntervalRef.current = setInterval(() => {
        fetchLatestScores();
      }, 5000);
      
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [selectedEvent, activeTab]);

  const fetchCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/public/competitions`);
      const comps = response.data.competitions || response.data || [];
      const arr = Array.isArray(comps) ? comps : [];
      setCompetitions(arr);

      // Deep-link: if ?event=<id> is in the URL (set by /:slug → SlugRedirect),
      // auto-open that event's detail view.
      try {
        const params = new URLSearchParams(window.location.search);
        const eventId = params.get('event');
        if (eventId) {
          const match = arr.find(c => c.id === eventId);
          if (match) {
            setSelectedEvent(match);
          }
        }
      } catch { /* no-op */ }
    } catch (error) {
      console.error('Error fetching competitions:', error);
      setCompetitions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRankings = async () => {
    if (!selectedEvent || !selectedCategory) return;
    
    // Only show loading spinner on initial load, not on auto-refresh
    if (isInitialRankingsLoad.current) {
      setLoadingRankings(true);
    }
    try {
      const response = await axios.get(`${API}/public/rankings/${selectedEvent.id}`, {
        params: {
          category: selectedCategory,
          apparatus: selectedApparatus
        }
      });
      setRankings(response.data.rankings || []);
      setQualification(response.data.qualification || null);
    } catch (error) {
      console.error('Error fetching rankings:', error);
      setRankings([]);
    } finally {
      setLoadingRankings(false);
      isInitialRankingsLoad.current = false;
    }
  };

  const fetchTeamRankings = async () => {
    if (!selectedEvent || !selectedCategory) return;
    if (isInitialRankingsLoad.current) setLoadingRankings(true);
    try {
      const res = await axios.get(`${API}/public/team-rankings/${selectedEvent.id}`, {
        params: { category: selectedCategory }
      });
      setTeamRankings(res.data);
    } catch (e) {
      setTeamRankings({ teams: [], apparatusList: [], teamCountingScores: 3, teamSize: 5 });
    } finally {
      setLoadingRankings(false);
      isInitialRankingsLoad.current = false;
    }
  };

  const fetchLatestScores = async () => {
    if (!selectedEvent) return;
    
    // Only show loading spinner on initial load, not on auto-refresh
    if (isInitialScoresLoad.current) {
      setLoadingScores(true);
    }
    try {
      const response = await axios.get(`${API}/public/latest-scores/${selectedEvent.id}`);
      setLatestScores(response.data.scores || []);
    } catch (error) {
      console.error('Error fetching latest scores:', error);
      setLatestScores([]);
    } finally {
      setLoadingScores(false);
      isInitialScoresLoad.current = false;
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const categorizeCompetitions = () => {
    const now = new Date();
    const current = [];
    const future = [];
    const past = [];

    competitions.forEach(comp => {
      const startDate = new Date(comp.startsOn || comp.startDate);
      const endDate = new Date(comp.endsOn || comp.endDate || comp.startsOn || comp.startDate);
      
      // Set end date to 23:59:59 of that day so competition shows until end of day
      endDate.setHours(23, 59, 59, 999);
      
      if (now >= startDate && now <= endDate) {
        current.push(comp);
      } else if (startDate > now) {
        future.push(comp);
      } else {
        past.push(comp);
      }
    });

    return { current, future, past };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return formatUKDate(dateString);
  };

  const { current, future, past } = categorizeCompetitions();

  const statusColorClass = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    blue: 'bg-blue-500'
  }[systemStatus.statusColor] || 'bg-green-500';

  const apparatusTabs = [
    { id: 'vault', label: 'Vault' },
    { id: 'bars', label: 'Bars' },
    { id: 'beam', label: 'Beam' },
    { id: 'floor', label: 'Floor' },
    { id: 'range', label: 'Range' },
    { id: 'aa', label: 'AA' }
  ];

  const handleGymnastClick = (gymnast, score, apparatus = null) => {
    if (selectedApparatus === 'aa' && !apparatus) return; // No detail for AA (unless coming from Latest Scores)
    if (!score || score.didNotCompete || score.finalScore === null) return;
    
    setSelectedGymnastDetail(gymnast);
    setSelectedScoreDetail(score);
    setSelectedApparatusForModal(apparatus || selectedApparatus);
    setScoreDetailOpen(true);
  };

  const getScoreDisplay = (ranking) => {
    if (selectedApparatus === 'aa') {
      if (ranking.aaScore === null || ranking.aaScore === undefined) {
        return <span className="text-gray-400">Not yet competed</span>;
      }
      return (
        <div className="flex flex-col items-end">
          <span className="font-semibold flex items-center gap-2">
            {parseFloat(ranking.aaScore || 0).toFixed(3)}
            {ranking.qualified && (
              <span className="bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded">Q</span>
            )}
          </span>
        </div>
      );
    }
    
    const score = ranking.score;
    if (!score) {
      return <span className="text-gray-400">Not yet competed</span>;
    }
    if (score.didNotCompete) {
      return <span className="text-gray-400">Did not compete (0.000)</span>;
    }
    if (score.finalScore === null || score.finalScore === undefined) {
      return <span className="text-gray-400">Not yet competed</span>;
    }
    return <span className="font-semibold">{parseFloat(score.finalScore || 0).toFixed(3)}</span>;
  };

  // Event detail view
  if (selectedEvent) {
    // If maintenance mode is on and user is on rankings/scores, reset to info
    if (systemStatus.maintenanceMode && (activeTab === 'rankings' || activeTab === 'scores')) {
      setActiveTab('info');
    }
    
    const startDate = selectedEvent.startsOn || selectedEvent.startDate;
    const endDate = selectedEvent.endsOn || selectedEvent.endDate || startDate;
    const venue = selectedEvent.publicVenue || selectedEvent.location || selectedEvent.venue || 'TBC';
    const address = selectedEvent.publicAddress || selectedEvent.venueAddress || '';
    const displayDates = selectedEvent.publicDates || `${formatDate(startDate)} - ${formatDate(endDate)}`;
    
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        {/* Development Banner */}
        <div className="bg-black text-white text-center py-1.5 sm:py-2 px-4 text-xs sm:text-sm font-medium">
          This system is currently in development. Some features may not be available yet.
        </div>

        {/* Maintenance Banner */}
        {systemStatus.maintenanceMode && (
          <div className="bg-yellow-500 text-black text-center py-2 px-4 text-xs sm:text-sm font-semibold" data-testid="maintenance-banner">
            Live Scores and Rankings are temporarily unavailable. Please check back shortly.
          </div>
        )}
        
        {/* Header */}
        <header className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4 cursor-pointer" onClick={() => setSelectedEvent(null)}>
              <img src="/scorz-logo.png" alt="Scorz" className="h-8 sm:h-12" />
            </div>
            
            <button 
              onClick={() => navigate('/auth')}
              className="flex items-center gap-1.5 sm:gap-2 text-gray-700 hover:text-black transition-colors"
            >
              <User className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-xs sm:text-sm">Login</span>
            </button>
          </div>
          
          {/* Event Tabs - Only 3 tabs */}
          <div className="bg-gray-100 border-t">
            <div className="max-w-7xl mx-auto px-2 sm:px-4">
              <div className="flex gap-1 overflow-x-auto">
                {[
                  { id: 'info', label: 'Info', mobileLabel: 'Info', icon: Info },
                  ...(!systemStatus.maintenanceMode ? [
                    { id: 'rankings', label: 'Live Rankings', mobileLabel: 'Rankings', icon: Trophy },
                    { id: 'scores', label: 'Latest Scores', mobileLabel: 'Scores', icon: Clock }
                  ] : [])
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${
                      activeTab === tab.id 
                        ? 'bg-black text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.mobileLabel}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </header>

        {/* Event Content */}
        <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 w-full">
          {activeTab === 'info' && (
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 sm:mb-6">Event Information</h2>
              
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-1 space-y-6">
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Event</h3>
                    <p className="text-gray-600">{selectedEvent.title}</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Venue</h3>
                    <p className="text-gray-600">{venue}</p>
                  </div>
                  
                  {address && (
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-1">Address</h3>
                      <p className="text-gray-600 whitespace-pre-line">{address}</p>
                    </div>
                  )}
                  
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Dates</h3>
                    <p className="text-gray-600">{displayDates}</p>
                  </div>
                  
                  {/* Live Stream Button */}
                  <div className="pt-4">
                    <button
                      onClick={() => setShowLiveStreamDialog(true)}
                      className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
                      data-testid="live-stream-btn"
                    >
                      <Video className="w-5 h-5" />
                      Live Stream
                    </button>
                  </div>
                </div>
                
                <div className="md:w-64 flex justify-center">
                  <img 
                    src={selectedEvent.organiserImage || "/scorz-logo.png"} 
                    alt="Organiser" 
                    className="w-32 h-32 object-contain"
                  />
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'rankings' && (
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 sm:mb-6">Live Rankings</h2>
              
              {!selectedCategory ? (
                // Category selection
                <div>
                  <p className="text-gray-500 mb-4">Select a category:</p>
                  {categories.length === 0 && teamCategories.length === 0 ? (
                    <p className="text-gray-400">No categories available for this event.</p>
                  ) : (
                    <ul className="space-y-2">
                      {categories.map((cat, index) => (
                        <li key={`ind-${index}`}>
                          <button
                            onClick={() => { setSelectedCategory(cat); setIsTeamCategory(false); setTeamRankings(null); }}
                            className="text-left w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded transition-colors"
                            data-testid={`category-${cat}`}
                          >
                            {cat}
                          </button>
                        </li>
                      ))}
                      {teamCategories.map((cat, index) => (
                        <li key={`team-${index}`}>
                          <button
                            onClick={() => { setSelectedCategory(cat); setIsTeamCategory(true); setRankings([]); }}
                            className="text-left w-full px-4 py-3 bg-black text-white hover:bg-gray-900 rounded transition-colors flex items-center justify-between"
                            data-testid={`team-category-${cat}`}
                          >
                            <span>{cat}</span>
                            <span className="text-xs font-bold bg-white text-black rounded px-2 py-0.5 ml-2">TEAM</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : isTeamCategory ? (
                // ── Team Rankings View ────────────────────────────────────
                <div>
                  <button
                    onClick={() => { setSelectedCategory(null); setIsTeamCategory(false); }}
                    className="flex items-center gap-1 text-gray-500 hover:text-black mb-4 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back to categories
                  </button>
                  <div className="flex items-center gap-3 mb-5">
                    <h3 className="font-semibold text-gray-800">{selectedCategory}</h3>
                    <span className="text-xs font-bold bg-black text-white rounded px-2 py-0.5">TEAM</span>
                    {teamRankings && (
                      <span className="text-xs text-gray-400">{teamRankings.teamSize} gymnasts · top {teamRankings.teamCountingScores} count</span>
                    )}
                  </div>
                  {loadingRankings ? (
                    <p className="text-gray-400 py-6 text-center">Loading team rankings…</p>
                  ) : !teamRankings || teamRankings.teams.length === 0 ? (
                    <p className="text-gray-400 py-6 text-center">No team scores yet — check back during the competition.</p>
                  ) : (
                    <TeamRankingsTable data={teamRankings} />
                  )}
                </div>
              ) : (
                // Rankings view
                <div>
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className="flex items-center gap-1 text-gray-500 hover:text-black mb-4 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back to categories
                  </button>
                  
                  <h3 className="font-semibold text-gray-800 mb-4">{selectedCategory}</h3>
                  
                  {/* Apparatus tabs */}
                  <div className="flex flex-wrap gap-1 mb-3 sm:mb-4 -mx-1">
                    {apparatusTabs.map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setSelectedApparatus(tab.id)}
                        className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded transition-colors ${
                          selectedApparatus === tab.id
                            ? 'bg-black text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        data-testid={`apparatus-${tab.id}`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  
                  {/* Club filter */}
                  <div className="flex flex-wrap gap-3 mb-4">
                    <select
                      value={filterClub}
                      onChange={(e) => setFilterClub(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="">All Clubs</option>
                      {clubs.map((club) => (
                        <option key={club.id} value={club.name}>{club.name}</option>
                      ))}
                    </select>
                    {filterClub && (
                      <button
                        onClick={() => setFilterClub('')}
                        className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
                      >
                        Clear Filter
                      </button>
                    )}
                  </div>
                  
                  {/* Qualification legend - only show when on AA and qualification is enabled */}
                  {selectedApparatus === 'aa' && qualification?.enabled && qualification?.text && (
                    <div className="mb-4 px-3 py-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                      <span className="bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded">Q</span>
                      <span className="text-sm text-green-800">= Qualified for {qualification.text}</span>
                    </div>
                  )}
                  
                  {/* Rankings list */}
                  {loadingRankings ? (
                    <p className="text-gray-500">Loading rankings...</p>
                  ) : rankings.length === 0 ? (
                    <p className="text-gray-400">No rankings available yet.</p>
                  ) : (
                    <div className="space-y-1">
                      {/* Desktop Header */}
                      <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 text-xs text-gray-500 font-medium border-b">
                        <div className="col-span-1">#</div>
                        <div className="col-span-1">No.</div>
                        <div className="col-span-4">Name</div>
                        <div className="col-span-3">Club</div>
                        <div className="col-span-3 text-right">Score</div>
                      </div>
                      {/* Mobile Header */}
                      <div className="sm:hidden grid grid-cols-12 gap-1 px-2 py-2 text-xs text-gray-500 font-medium border-b">
                        <div className="col-span-2">#</div>
                        <div className="col-span-6">Gymnast</div>
                        <div className="col-span-4 text-right">Score</div>
                      </div>
                      {rankings
                        .filter(ranking => !filterClub || ranking.club === filterClub)
                        .map((ranking, idx) => {
                        // Use backend rank if available, fallback to index+1
                        const position = ranking.rank || (idx + 1);
                        const getMedalStyle = () => {
                          if (position === 1) return 'bg-yellow-400 text-yellow-900'; // Gold
                          if (position === 2) return 'bg-gray-300 text-gray-700';     // Silver
                          if (position === 3) return 'bg-amber-600 text-amber-100';   // Bronze
                          return 'bg-gray-100 text-gray-500';
                        };
                        
                        return (
                        <React.Fragment key={ranking.gymnastId || idx}>
                          {/* Desktop Row */}
                          <div 
                            className={`hidden sm:grid grid-cols-12 gap-2 px-3 py-2 text-sm hover:bg-gray-50 rounded ${
                              selectedApparatus !== 'aa' && ranking.score && !ranking.score.didNotCompete && ranking.score.finalScore !== null
                                ? 'cursor-pointer'
                                : ''
                            }`}
                            onClick={() => handleGymnastClick(ranking, ranking.score)}
                          >
                            <div className="col-span-1">
                              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${getMedalStyle()}`}>
                                {position}
                              </span>
                            </div>
                            <div className="col-span-1 font-medium">{ranking.number || '-'}</div>
                            <div className="col-span-4">{ranking.name}</div>
                            <div className="col-span-3 text-gray-600 truncate">{ranking.club}</div>
                            <div className="col-span-3 text-right">{getScoreDisplay(ranking)}</div>
                          </div>
                          {/* Mobile Row */}
                          <div 
                            className={`sm:hidden grid grid-cols-12 gap-1 px-2 py-2 text-sm hover:bg-gray-50 rounded ${
                              selectedApparatus !== 'aa' && ranking.score && !ranking.score.didNotCompete && ranking.score.finalScore !== null
                                ? 'cursor-pointer'
                                : ''
                            }`}
                            onClick={() => handleGymnastClick(ranking, ranking.score)}
                          >
                            <div className="col-span-2 flex items-center gap-1">
                              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${getMedalStyle()}`}>
                                {position}
                              </span>
                            </div>
                            <div className="col-span-6">
                              <div className="font-medium text-sm truncate">{ranking.name}</div>
                              <div className="text-xs text-gray-500 truncate">{ranking.club}</div>
                            </div>
                            <div className="col-span-4 text-right font-semibold">{getScoreDisplay(ranking)}</div>
                          </div>
                        </React.Fragment>
                        );
                      })}
                      {rankings.filter(ranking => !filterClub || ranking.club === filterClub).length === 0 && (
                        <p className="text-gray-500 text-center py-4">No rankings match the selected filter.</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'scores' && (
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 gap-2">
                <h2 className="text-base sm:text-lg font-semibold text-gray-700">Latest Scores</h2>
                <p className="text-xs text-gray-500">Auto-refreshes every 5 seconds</p>
              </div>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-4">
                <select
                  value={filterClub}
                  onChange={(e) => setFilterClub(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-black focus:border-transparent"
                >
                  <option value="">All Clubs</option>
                  {clubs.map((club) => (
                    <option key={club.id} value={club.name}>{club.name}</option>
                  ))}
                </select>
                {filterClub && (
                  <button
                    onClick={() => setFilterClub('')}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
              
              {loadingScores && latestScores.length === 0 ? (
                <p className="text-gray-500">Loading scores...</p>
              ) : latestScores.length === 0 ? (
                <p className="text-gray-500">No scores yet. Scores will appear here as they are submitted.</p>
              ) : (
                <div className="space-y-2">
                  {latestScores
                    .filter(score => {
                      // Apply club filter only
                      if (filterClub && score.clubName !== filterClub) return false;
                      return true;
                    })
                    .map((score, index) => (
                    <div 
                      key={score.id || index}
                      className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-lg cursor-pointer hover:ring-2 hover:ring-black transition-all ${
                        index === 0 ? 'bg-green-50 border-l-4 border-l-green-500' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      onClick={() => {
                        setSelectedGymnastDetail({
                          gymnastNumber: score.gymnastNumber,
                          gymnastName: score.gymnastName,
                          clubName: score.clubName
                        });
                        setSelectedScoreDetail(score);
                        setSelectedApparatusForModal(score.apparatus);
                        setScoreDetailOpen(true);
                      }}
                    >
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="text-xl sm:text-2xl font-bold text-gray-700 w-10 sm:w-12 shrink-0">
                          {score.gymnastNumber || '-'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{score.gymnastName || 'Unknown'}</p>
                          <p className="text-xs sm:text-sm text-gray-500 truncate">{score.clubName || 'Unknown Club'}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 mt-2 sm:mt-0 pl-10 sm:pl-0">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          score.apparatus === 'vault' ? 'bg-purple-100 text-purple-800' :
                          score.apparatus === 'bars' ? 'bg-blue-100 text-blue-800' :
                          score.apparatus === 'beam' ? 'bg-pink-100 text-pink-800' :
                          score.apparatus === 'floor' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {score.apparatus?.toUpperCase() || '-'}
                        </span>
                        <div className="text-right">
                          <p className="text-xl sm:text-2xl font-bold">
                            {parseFloat(score.finalScore || 0).toFixed(3)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {score.createdAt ? formatUKTime(score.createdAt) : ''}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {latestScores.filter(score => {
                    if (filterClub && score.clubName !== filterClub) return false;
                    return true;
                  }).length === 0 && (
                    <p className="text-gray-500 text-center py-4">No scores match the selected filter.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </main>

        {/* Footer */}
        <footer>
          <div className="bg-black text-white text-center py-4">
            <p className="text-sm px-4">
              All scores may be subject to change or protest and are provided without guarantee as to their accuracy or completeness.
            </p>
          </div>
          <div className="bg-white text-center py-2 border-t">
            <div className="flex items-center justify-center gap-1.5 flex-wrap px-2">
              <div className="flex items-center gap-1" data-testid="system-status-indicator">
                <div className={`w-1.5 h-1.5 rounded-full ${statusColorClass} animate-pulse`} />
                <span className="text-xs text-gray-600">{systemStatus.statusText}</span>
              </div>
              <span className="text-gray-400 mx-0.5">|</span>
              <button 
                onClick={() => setPrivacyOpen(true)}
                className="text-xs text-gray-600 hover:text-black underline"
              >
                Privacy Notice
              </button>
              <span className="text-gray-400 mx-0.5">|</span>
              <span className="text-xs text-gray-600">&copy; 2025 - 2026</span>
            </div>
          </div>
        </footer>
        
        <PrivacyNoticeModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} />
        <ScoreDetailModal 
          isOpen={scoreDetailOpen} 
          onClose={() => setScoreDetailOpen(false)}
          gymnast={selectedGymnastDetail}
          apparatus={selectedApparatusForModal || selectedApparatus}
          score={selectedScoreDetail}
          scoreConfig={selectedEvent}
        />
        
        {/* Live Stream Dialog */}
        <Dialog open={showLiveStreamDialog} onOpenChange={setShowLiveStreamDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Video className="w-5 h-5 text-red-600" />
                Live Stream
              </DialogTitle>
            </DialogHeader>
            <div className="py-6 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Video className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600">
                Live Stream is not available for this competition.
              </p>
            </div>
            <div className="flex justify-center">
              <button
                onClick={() => setShowLiveStreamDialog(false)}
                className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Close
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main events listing view
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Development Banner */}
      <div className="bg-black text-white text-center py-1.5 sm:py-2 px-4 text-xs sm:text-sm font-medium">
        This system is currently in development. Some features may not be available yet.
      </div>
      
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <img src="/scorz-logo.png" alt="Scorz" className="h-8 sm:h-12" />
          </div>
          
          <button 
            onClick={() => navigate('/auth')}
            className="flex items-center gap-1.5 sm:gap-2 text-gray-700 hover:text-black transition-colors"
          >
            <User className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-xs sm:text-sm">Login</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 w-full">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Loading events...</div>
          </div>
        ) : (
          <div className="space-y-6 sm:space-y-8">
            {/* Current Events */}
            <section>
              <button 
                onClick={() => toggleSection('current')}
                className="flex items-center gap-2 text-xl sm:text-3xl font-light text-gray-700 mb-3 sm:mb-4 hover:text-gray-900 transition-colors"
              >
                Current Events
                {expandedSections.current ? <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6" /> : <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6" />}
              </button>
              
              {expandedSections.current && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {current.length === 0 ? (
                    <p className="text-gray-500 col-span-full">No current events</p>
                  ) : (
                    current.map(comp => (
                      <EventCard key={comp.id} event={comp} onClick={() => setSelectedEvent(comp)} formatDate={formatDate} />
                    ))
                  )}
                </div>
              )}
            </section>

            {/* Future Events */}
            <section>
              <button 
                onClick={() => toggleSection('future')}
                className="flex items-center gap-2 text-xl sm:text-3xl font-light text-gray-700 mb-3 sm:mb-4 hover:text-gray-900 transition-colors"
              >
                Future Events
                {expandedSections.future ? <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6" /> : <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6" />}
              </button>
              
              {expandedSections.future && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {future.length === 0 ? (
                    <p className="text-gray-500 col-span-full">No upcoming events</p>
                  ) : (
                    future.map(comp => (
                      <EventCard key={comp.id} event={comp} onClick={() => setSelectedEvent(comp)} formatDate={formatDate} />
                    ))
                  )}
                </div>
              )}
            </section>

            {/* Past Events */}
            <section>
              <button 
                onClick={() => toggleSection('past')}
                className="flex items-center gap-2 text-xl sm:text-3xl font-light text-gray-700 mb-3 sm:mb-4 hover:text-gray-900 transition-colors"
              >
                Past Events
                {expandedSections.past ? <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6" /> : <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6" />}
              </button>
              
              {expandedSections.past && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {past.length === 0 ? (
                    <p className="text-gray-500 col-span-full">No past events</p>
                  ) : (
                    past.map(comp => (
                      <EventCard key={comp.id} event={comp} onClick={() => setSelectedEvent(comp)} formatDate={formatDate} />
                    ))
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer>
        <div className="bg-black text-white text-center py-4">
          <p className="text-sm px-4">
            All scores may be subject to change or protest and are provided without guarantee as to their accuracy or completeness.
          </p>
        </div>
        <div className="bg-white text-center py-2 border-t">
          <div className="flex items-center justify-center gap-1.5 flex-wrap px-2">
            <div className="flex items-center gap-1" data-testid="system-status-indicator-main">
              <div className={`w-1.5 h-1.5 rounded-full ${statusColorClass} animate-pulse`} />
              <span className="text-xs text-gray-600">{systemStatus.statusText}</span>
            </div>
            <span className="text-gray-400 mx-0.5">|</span>
            <button 
              onClick={() => setPrivacyOpen(true)}
              className="text-xs text-gray-600 hover:text-black underline"
            >
              Privacy Notice
            </button>
            <span className="text-gray-400 mx-0.5">|</span>
            <span className="text-xs text-gray-600">&copy; 2025 - 2026</span>
          </div>
        </div>
      </footer>
      
      <PrivacyNoticeModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </div>
  );
}

// Event Card Component
function EventCard({ event, onClick, formatDate }) {
  const startDate = event.startsOn || event.startDate;
  const endDate = event.endsOn || event.endDate || startDate;
  const venue = event.publicVenue || event.location || event.venue || 'Venue TBC';
  const displayDates = event.publicDates || `${formatDate(startDate)} - ${formatDate(endDate)}`;

  return (
    <Card 
      className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-l-4 border-l-gray-300"
      onClick={onClick}
      data-testid={`event-card-${event.id}`}
    >
      <CardContent className="p-4">
        <h3 className="font-semibold text-gray-800 text-lg mb-1">{event.title}</h3>
        <div className="flex items-center gap-2 text-gray-600 text-sm mb-3">
          <MapPin className="w-4 h-4" />
          <span>{venue}</span>
        </div>
        
        <div className="flex items-start gap-3 mt-4">
          <img 
            src={event.organiserImage || "/scorz-logo.png"} 
            alt="Organiser" 
            className="w-12 h-12 object-contain"
          />
          <div>
            <p className="text-xs text-gray-500">Event Dates:</p>
            <p className="text-sm text-gray-700">{displayDates}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Apparatus display labels for team rankings
const APP_LABELS = { vault: 'VT', bars: 'UB', beam: 'BB', floor: 'FX', range: 'Range' };

function TeamRankingsTable({ data }) {
  const [expandedTeam, setExpandedTeam] = useState(null);
  const { teams = [], apparatusList = [], teamCountingScores = 3 } = data;

  return (
    <div className="overflow-x-auto" data-testid="team-rankings-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2 border-gray-300">
            <th className="text-left py-2 pl-1 w-8 text-gray-500 font-medium">#</th>
            <th className="text-left py-2 font-semibold text-gray-800">Team</th>
            {apparatusList.map(app => (
              <th key={app} className="text-right py-2 px-1 font-semibold text-gray-800 whitespace-nowrap">{APP_LABELS[app] || app.toUpperCase()}</th>
            ))}
            <th className="text-right py-2 px-1 font-semibold text-gray-800">AA</th>
            <th className="w-6"></th>
          </tr>
        </thead>
        <tbody>
          {teams.map((team) => (
            <React.Fragment key={team.id}>
              <tr
                className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => setExpandedTeam(expandedTeam === team.id ? null : team.id)}
                data-testid={`team-row-${team.id}`}
              >
                <td className="py-2 pl-1 text-gray-500 font-bold">{team.rank}</td>
                <td className="py-2 font-semibold text-gray-900">{team.name}</td>
                {apparatusList.map(app => (
                  <td key={app} className="py-2 px-1 text-right font-mono text-gray-800 tabular-nums">
                    {team.perApparatus?.[app]?.subtotal != null
                      ? team.perApparatus[app].subtotal.toFixed(3)
                      : <span className="text-gray-300">—</span>}
                  </td>
                ))}
                <td className="py-2 px-1 text-right font-black text-gray-900 tabular-nums">{team.total.toFixed(3)}</td>
                <td className="py-2 px-1 text-gray-400 text-xs">{expandedTeam === team.id ? '▲' : '▼'}</td>
              </tr>
              {expandedTeam === team.id && (
                <tr>
                  <td colSpan={apparatusList.length + 3} className="bg-gray-50 px-3 py-3">
                    <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">
                      Individual scores — top {teamCountingScores} per apparatus count
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {apparatusList.map(app => {
                        const appData = team.perApparatus?.[app];
                        if (!appData || appData.gymnasts.length === 0) return null;
                        return (
                          <div key={app}>
                            <p className="text-xs font-bold text-gray-700 mb-1">{APP_LABELS[app] || app.toUpperCase()}</p>
                            {appData.gymnasts.map((g, i) => (
                              <div key={g.gymnastId} className={`flex justify-between text-xs py-0.5 ${g.counts ? 'font-semibold text-gray-900' : 'text-gray-400 line-through'}`}>
                                <span>{g.name} <span className="font-normal text-gray-400 no-underline" style={{textDecoration:'none'}}>{g.clubName}</span></span>
                                <span className="tabular-nums font-mono">{g.score.toFixed(3)}{g.counts ? ' ✓' : ''}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
